import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import cn from 'classnames'
import { Info, X } from 'lucide-react'
import { IOrdinalMarketSummary } from 'api/api'
import { useBsvExchangeRate, satsToUsd, formatUsdValue } from 'api/useBsvExchangeRate'
import { marketFeeBps, marketFeePercentLabel, marketSellerProceedsSats } from 'lib/marketFee'
import useOrdinalDescriptor from 'hooks/useOrdinalDescriptor'
import styles from './market-order-modals.module.scss'

export const MIN_ORDER_SATS = 1000

// The sale-fee notice on the list tray. The fee comes out of the seller's
// proceeds, so only the seller is told; the buyer pays the price shown.
function MarketFeeNotice({ children }: { children: React.ReactNode }) {
    return (
        <div className={styles.feeNotice} role="note">
            <Info className={styles.feeNoticeIcon} size={15} strokeWidth={2.4} aria-hidden />
            <span>{children}</span>
        </div>
    )
}

// Local copy of the rail's compact formatter — importing it from
// OrdinalMarketControls would create a module cycle.
function formatSatsCompact(sats: number | null | undefined): string {
    const n = Number(sats || 0)
    if (n >= 1_000_000) {
        const m = n / 1_000_000
        return `${m >= 10 ? Math.round(m) : Math.round(m * 10) / 10}M`
    }
    if (n >= 1_000) {
        const k = n / 1_000
        return `${k >= 100 ? Math.round(k) : Math.round(k * 10) / 10}k`
    }
    return String(n)
}

// Shared plumbing for the market modals: portal to body, scrim + Escape close,
// and React-tree propagation stopped so taps never reach the tile underneath.
function ModalShell({
    onClose,
    ariaLabel,
    sheetClassName,
    preventClose = false,
    children,
}: {
    onClose: () => void
    ariaLabel: string
    sheetClassName: string
    preventClose?: boolean
    children: React.ReactNode
}) {
    const sheetRef = useRef<HTMLDivElement>(null)
    const onCloseRef = useRef(onClose)
    const preventCloseRef = useRef(preventClose)
    onCloseRef.current = onClose
    preventCloseRef.current = preventClose

    useEffect(() => {
        const previouslyFocused =
            document.activeElement instanceof HTMLElement ? document.activeElement : null
        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        const focusableElements = () =>
            Array.from(
                sheetRef.current?.querySelectorAll<HTMLElement>(
                    'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
                ) || [],
            ).filter(element => element.getClientRects().length > 0)

        const focusFrame = window.requestAnimationFrame(() => {
            if (!sheetRef.current?.contains(document.activeElement)) {
                const focusTarget = focusableElements()[0] || sheetRef.current
                focusTarget?.focus()
            }
        })

        const onKeyDown = (ev: KeyboardEvent) => {
            if (ev.key === 'Escape') {
                ev.preventDefault()
                ev.stopPropagation()
                if (!preventCloseRef.current) onCloseRef.current()
                return
            }

            if (ev.key === 'Tab') {
                const focusable = focusableElements()
                if (focusable.length === 0) {
                    ev.preventDefault()
                    sheetRef.current?.focus()
                    return
                }

                const first = focusable[0]
                const last = focusable[focusable.length - 1]
                const active = document.activeElement
                if (ev.shiftKey && (active === first || !sheetRef.current?.contains(active))) {
                    ev.preventDefault()
                    last.focus()
                } else if (
                    !ev.shiftKey &&
                    (active === last || !sheetRef.current?.contains(active))
                ) {
                    ev.preventDefault()
                    first.focus()
                }
            }
        }
        document.addEventListener('keydown', onKeyDown, true)
        return () => {
            window.cancelAnimationFrame(focusFrame)
            document.removeEventListener('keydown', onKeyDown, true)
            document.body.style.overflow = previousOverflow
            if (previouslyFocused?.isConnected) previouslyFocused.focus()
        }
    }, [])

    const stop = (ev: React.SyntheticEvent) => ev.stopPropagation()

    return createPortal(
        <div
            className={styles.overlay}
            onClick={ev => {
                ev.stopPropagation()
                if (!preventClose) onClose()
            }}
            onMouseDown={stop}
            onPointerDown={stop}
            onTouchStart={stop}
        >
            <div
                ref={sheetRef}
                className={sheetClassName}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabel}
                aria-busy={preventClose || undefined}
                tabIndex={-1}
                onClick={stop}
                onMouseDown={stop}
                onPointerDown={stop}
                onTouchStart={stop}
            >
                {children}
            </div>
        </div>,
        document.body,
    )
}

function OrdinalThumb({ origin, className }: { origin?: string | null; className: string }) {
    const { data: descriptor } = useOrdinalDescriptor(origin, { assumeOrigin: true })
    const src = descriptor?.preview_url || descriptor?.content_url
    const isImage = String(descriptor?.content_type || '')
        .toLowerCase()
        .startsWith('image/')
    if (src && (isImage || descriptor?.preview_url)) {
        return <img className={className} src={src} alt="Ordinal" />
    }
    return <div className={cn(className, styles.thumbFallback)} />
}

/**
 * Amount entry for placing an offer or listing for sale — the "market tray".
 * One hero number and quick-raise chips from the live spread.
 */
export function MarketEntryTray({
    mode,
    origin,
    market,
    initialPriceSats,
    busy,
    error,
    onSubmit,
    onClearError,
    onClose,
}: {
    mode: 'offer' | 'list'
    origin?: string | null
    market?: IOrdinalMarketSummary | null
    initialPriceSats?: number | null
    busy: boolean
    error: string | null
    onSubmit: (priceSats: number) => void
    onClearError: () => void
    onClose: () => void
}) {
    const [amountInput, setAmountInput] = useState(
        initialPriceSats ? initialPriceSats.toLocaleString() : '',
    )
    const exchangeRate = useBsvExchangeRate({ enabled: true })
    const errorId = useId()

    const normalized = amountInput.replace(/[,_\s]/g, '')
    const amount = /^\d+$/.test(normalized) ? Number(normalized) : NaN
    const valid = Number.isSafeInteger(amount) && amount >= MIN_ORDER_SATS
    const usdEcho = valid && exchangeRate ? formatUsdValue(satsToUsd(amount, exchangeRate)) : null
    // A seller sees what the sale leaves them: the price minus Treechat's cut.
    const feeNotice =
        mode === 'list' && marketFeeBps() > 0
            ? valid
                ? `Treechat fee ${marketFeePercentLabel()} · you receive ${marketSellerProceedsSats(
                      amount,
                  ).toLocaleString()} sats`
                : `Treechat takes a ${marketFeePercentLabel()} fee from the sale price`
            : null

    const askSats = market?.ask?.price_sats || null
    const topBidSats = market?.top_bid_sats || null
    const beatTopSats = topBidSats ? Math.ceil((topBidSats * 1.1) / 100) * 100 : null

    const chips = useMemo(() => {
        if (mode !== 'offer') return []
        const list: { label: string; sats: number }[] = []
        if (beatTopSats)
            list.push({ label: `Beat top · ${formatSatsCompact(beatTopSats)}`, sats: beatTopSats })
        if (askSats && (!beatTopSats || askSats > beatTopSats)) {
            list.push({ label: `Match list · ${formatSatsCompact(askSats)}`, sats: askSats })
        }
        return list
    }, [mode, beatTopSats, askSats])

    const submit = useCallback(() => {
        if (valid && !busy) onSubmit(amount)
    }, [valid, busy, onSubmit, amount])

    const updateAmount = useCallback(
        (value: string) => {
            setAmountInput(value)
            onClearError()
        },
        [onClearError],
    )

    const isUpdate = Number.isSafeInteger(initialPriceSats) && Number(initialPriceSats) > 0
    const title =
        mode === 'offer'
            ? isUpdate
                ? 'Change offer'
                : 'Make an offer'
            : isUpdate
            ? 'Change price'
            : 'List for sale'
    const describedBy = error ? errorId : undefined

    return (
        <ModalShell
            onClose={onClose}
            ariaLabel={title}
            sheetClassName={styles.entrySheet}
            preventClose={busy}
        >
            <div className={styles.grabber} />
            <div className={styles.headerRow}>
                <OrdinalThumb origin={origin} className={styles.thumb} />
                <div className={styles.headerMeta}>
                    <span className={styles.title}>{title}</span>
                    <span className={styles.spread}>
                        {askSats ? (
                            <span className={styles.askText}>
                                Listed {formatSatsCompact(askSats)}
                            </span>
                        ) : null}
                        {askSats && topBidSats ? ' · ' : null}
                        {topBidSats ? (
                            <span className={styles.bidText}>
                                Top {formatSatsCompact(topBidSats)}
                            </span>
                        ) : null}
                        {!askSats && !topBidSats ? 'No open orders yet' : null}
                    </span>
                </div>
                <button
                    type="button"
                    className={styles.closeButton}
                    aria-label="Close"
                    disabled={busy}
                    onClick={onClose}
                >
                    <X size={18} strokeWidth={2.4} />
                </button>
            </div>

            <div className={styles.heroField}>
                <div className={styles.heroLine}>
                    <input
                        className={styles.heroInput}
                        inputMode="numeric"
                        aria-label={mode === 'list' ? 'Listing price in sats' : 'Offer in sats'}
                        aria-describedby={describedBy}
                        placeholder={MIN_ORDER_SATS.toLocaleString()}
                        value={amountInput}
                        autoFocus
                        onChange={ev => updateAmount(ev.target.value)}
                        onKeyDown={ev => {
                            if (ev.key === 'Enter') submit()
                        }}
                    />
                    <span className={styles.heroUnit}>sats</span>
                </div>
                <span className={styles.heroEcho}>{usdEcho || ' '}</span>
            </div>

            {feeNotice && <MarketFeeNotice>{feeNotice}</MarketFeeNotice>}

            {chips.length > 0 && (
                <div className={styles.chipRow}>
                    {chips.map(chip => (
                        <button
                            key={chip.label}
                            type="button"
                            className={cn(styles.chip, amount === chip.sats && styles.chipActive)}
                            onClick={() => updateAmount(chip.sats.toLocaleString())}
                        >
                            {chip.label}
                        </button>
                    ))}
                </div>
            )}

            {error && (
                <div className={styles.error} id={errorId} role="alert">
                    {error}
                </div>
            )}

            <button
                type="button"
                className={styles.primaryButton}
                disabled={!valid || busy}
                onClick={submit}
            >
                {busy
                    ? 'Working…'
                    : valid
                    ? `${
                          isUpdate ? 'Update' : mode === 'offer' ? 'Place offer' : 'List'
                      } · ${amount.toLocaleString()} sats`
                    : isUpdate
                    ? 'Update'
                    : mode === 'offer'
                    ? 'Place offer'
                    : 'List'}
            </button>
            <button type="button" className={styles.cancelButton} disabled={busy} onClick={onClose}>
                Cancel
            </button>
        </ModalShell>
    )
}

/**
 * Final confirmation for the irreversible moments — the "trade ticket".
 * Artwork pane, one hero number, one line of consequences.
 */
export function MarketConfirmTicket({
    kind,
    origin,
    priceSats,
    listPriceSats,
    bidderName,
    busy,
    error,
    onConfirm,
    onClose,
}: {
    kind: 'buy' | 'accept'
    origin?: string | null
    priceSats: number
    listPriceSats?: number | null
    bidderName?: string | null
    busy: boolean
    error: string | null
    onConfirm: () => void
    onClose: () => void
}) {
    const exchangeRate = useBsvExchangeRate({ enabled: true })
    const usd = exchangeRate ? formatUsdValue(satsToUsd(priceSats, exchangeRate)) : null
    const pctOfList =
        kind === 'accept' && listPriceSats && listPriceSats > 0
            ? Math.round((priceSats / listPriceSats) * 100)
            : null

    // The hero is the sale price; the seller's take after Treechat's cut sits
    // beneath it, in the same sats the settlement pays out.
    const feeLine =
        kind === 'accept' && marketFeeBps() > 0
            ? `you receive ${marketSellerProceedsSats(
                  priceSats,
              ).toLocaleString()} sats after the ${marketFeePercentLabel()} Treechat fee`
            : null
    const subParts =
        kind === 'buy'
            ? [usd].filter(Boolean)
            : [usd, pctOfList !== null ? `${pctOfList}% of your list price` : null, feeLine].filter(
                  Boolean,
              )
    const eyebrowText =
        kind === 'buy' ? 'Buy now' : bidderName ? `Sell to ${bidderName}` : 'Sell now'
    const consequence =
        kind === 'buy'
            ? 'Payment and ordinal transfer happen together in one transaction'
            : 'One transaction · ends your listing and other offers · posts a sale card'

    return (
        <ModalShell
            onClose={onClose}
            ariaLabel={kind === 'buy' ? 'Buy ordinal' : 'Sell ordinal'}
            sheetClassName={styles.ticketSheet}
            preventClose={busy}
        >
            <div className={styles.ticketArt}>
                <OrdinalThumb origin={origin} className={styles.ticketArtMedia} />
            </div>
            <div className={styles.ticketBody}>
                <span className={styles.eyebrow}>{eyebrowText}</span>
                <div className={styles.ticketHero}>
                    <span
                        className={cn(
                            styles.ticketPrice,
                            kind === 'accept' && styles.ticketPriceIn,
                        )}
                    >
                        {kind === 'accept' ? '+' : ''}
                        {priceSats.toLocaleString()}
                    </span>
                    <span className={styles.ticketUnit}>sats</span>
                </div>
                {subParts.length > 0 && (
                    <span className={styles.ticketSub}>{subParts.join(' · ')}</span>
                )}
                <span className={styles.ticketConsequence}>{consequence}</span>
                {error && (
                    <div className={styles.error} role="alert">
                        {error}
                    </div>
                )}
                <div className={styles.ticketActions}>
                    <button
                        type="button"
                        className={styles.ghostButton}
                        disabled={busy}
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={styles.primaryButton}
                        disabled={busy}
                        onClick={onConfirm}
                    >
                        {busy ? 'Working…' : kind === 'buy' ? 'Buy now' : 'Sell now'}
                    </button>
                </div>
            </div>
        </ModalShell>
    )
}
